/* TODO: t() */
import React, {
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  NativeSyntheticEvent,
  TextInput as TextInputNative,
  TextInputFocusEventData,
  TextInputProps,
  StyleSheet,
} from 'react-native';

import { themeColors } from './Constants';
import { useSubmitForm } from './Form';

type NativeElement = HTMLInputElement | HTMLTextAreaElement;

export type TextInputType = {
  getNativeHandle: () => NativeElement | null;
  focus: () => void;
};

type Props = Omit<
  TextInputProps,
  'nativeID' | 'onBlur' | 'onChange' | 'value'
> & {
  id?: string;
  type?: 'text' | 'search';
  disabled?: boolean;
  name?: string;
  tabIndex?: number;
  backgroundColor?: string;
  selectOnFocus?: boolean;
  // NOTE: Added support for null/undefined to squash an error with editing vendors.
  value?: string | null | undefined;
  onChange?: (event: React.ChangeEvent<NativeElement>) => void;
  onBlur?: (event?: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  invokeOnBlurBeforeSubmit?: boolean;
};

export { Props as TextInputProps };

function TextInput(
  props: Props,
  forwardedRef: React.ForwardedRef<TextInputType>,
) {
  let {
    id,
    name,
    tabIndex,
    style,
    multiline,
    backgroundColor,
    selectOnFocus,
    invokeOnBlurBeforeSubmit = true,
    onBlur,
    onChange,
    onSubmitEditing,
    value,
    ...otherProps
  } = props;
  let ref = useRef<NativeElement>(null);
  // TODO: Use the autoFocus prop that's built in to TextInput
  useEffect(() => {
    if (props.autoFocus) {
      // For some reason, it seems we need a delay here.
      let timeout = setTimeout(() => {
        ref.current && ref.current.focus();
      }, 50);
      return () => {
        clearTimeout(timeout);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // As of time of writing, only used in one place: components/Evaluation/FormElements/TextInputElement/EditView.tsx
  useEffect(() => {
    if (ref.current) {
      if (name == null) {
        ref.current.removeAttribute('name');
      } else {
        ref.current.name = name;
      }
    }
  }, [name]);
  // This is basically a duplicate of useTabIndex() implementation
  useEffect(() => {
    if (ref.current) {
      if (tabIndex == null) {
        ref.current.removeAttribute('tabIndex');
      } else {
        ref.current.tabIndex = tabIndex;
      }
    }
  }, [tabIndex]);
  useImperativeHandle(forwardedRef, () => ({
    getNativeHandle: () => ref.current,
    focus: () => {
      ref.current?.focus();
    },
  }));
  useSelectOnFocus(ref, selectOnFocus == null ? true : selectOnFocus);
  let submitForm = useSubmitForm();
  let backgroundColorStyle =
    backgroundColor != null ? { backgroundColor } : undefined;
  return (
    <TextInputNative
      {...otherProps}
      nativeID={id}
      ref={ref as never}
      multiline={multiline}
      value={value ?? undefined}
      onChange={onChange as never}
      onBlur={onBlur}
      onSubmitEditing={(event) => {
        invokeOnBlurBeforeSubmit && onBlur?.();
        onSubmitEditing?.(event);
        submitForm?.();
      }}
      style={[
        styles.root,
        !multiline && styles.singleLine,
        style,
        backgroundColorStyle,
      ]}
    />
  );
}

function useSelectOnFocus(
  ref: React.RefObject<NativeElement>,
  selectOnFocus: void | boolean,
) {
  let recentMouseDown = useRef(false);
  let selectOnFocusRef = useRef(selectOnFocus);
  selectOnFocusRef.current = selectOnFocus;

  let onMouseDown = useCallback(() => {
    recentMouseDown.current = true;
    setImmediate(() => {
      recentMouseDown.current = false;
    });
  }, []);

  let onFocus = useCallback((event: Event) => {
    let textArea = (event.target as unknown) as HTMLTextAreaElement;
    // We want to select the text inside a textarea when you tab focus to it,
    // but NOT when you click inside it. This replicates the behavior of
    // <input type="text" />.
    // When you click inside a textarea that does not currently have focus, the
    // following events fire (in one tick):
    //  1. mousedown
    //  2. textarea focus
    // So we should not change the selection if the mouse event fired.
    if (
      selectOnFocusRef.current === true &&
      recentMouseDown.current === false
    ) {
      // For some reason we can't select until next tick or it will de-select
      // immediately [Chrome].
      setImmediate(() => {
        // When a textarea is focused and you click the location bar and then
        // click the textarea again, the following events fire (in one tick):
        //  1. window focus
        //  2. textarea focus (even if it was already focused)
        //  3. mousedown
        // So we should not change the selection if the mouse event fired, and
        // we should ensure it is still the active element before selecting.
        if (
          recentMouseDown.current === false &&
          textArea === document.activeElement
        ) {
          textArea.select();
        }
      });
    }
  }, []);
  useEffect(() => {
    let textArea = (ref.current as unknown) as
      | HTMLTextAreaElement
      | HTMLInputElement
      | null;
    if (textArea && textArea.tagName === 'TEXTAREA') {
      textArea.addEventListener('mousedown', onMouseDown);
      textArea.addEventListener('focus', onFocus);
      return () => {
        textArea?.removeEventListener('mousedown', onMouseDown);
        textArea?.removeEventListener('focus', onFocus);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

let styles = StyleSheet.create({
  root: {
    backgroundColor: themeColors.white,
    borderWidth: 1,
    borderColor: themeColors.borderGrey,
    borderRadius: 3,
    paddingVertical: 6,
    paddingHorizontal: 12,
    lineHeight: 20,
    boxSizing: 'border-box',
    fontWeight: 'normal',
  },
  singleLine: {
    height: 34,
  },
});

export default forwardRef(TextInput);
